import { ShoppingCart } from "@mui/icons-material";
import { orderBy } from "lodash";
import { CollapsableProvider } from "controls/global/collapsable-form-section/CollapsableProvider";
import FormSection from "controls/global/form-section";
import TabNavigator from "controls/global/tab-navigator";
import { TabNavigatorItem, TitleSearchProduct } from "entities/UIModel";
import { isEqual } from "lodash";
import {
  ActionType,
  useSideNavProvider
} from "pages/file/SideNavProvider";
import { isProductSectionAvailable } from "pages/file/utils/productActionHelper";
import React, {
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  useFormState,
  useWatch,
} from "react-hook-form";
import GradientIcon from "theme/icons/GradientIcon";
import { useCompanyProductOptions } from "utils/context/CompanyProductOptionsContext";
import { useSuccessorLanguages } from "utils/context/CompanySuccessorLanguageContext";
import { useConfigContext } from "utils/context/ConfigContextProvider";
// import { useProductAgenycLocations } from "utils/context/ProductAgencyLocationsContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { useSpaceAgencyConfig } from "utils/context/SpaceContext";
import { useTranslation } from "utils/context/TranslationContext";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import {
  PERMISSIONS,
  ProductTabNameExtension,
  ProductTabTitle,
  ProductType,
  ProductTypeName,
} from "utils/data/enum";
import { getProductTabSortOrder } from "pages/file/utils/productActionHelper";
import { tabMapping } from "utils/tabNavigator";
import ProductOption from "entities/UIModel/company/ProductOption";
import { useCompanyLocation, useCompanyLocationActions } from "utils/context/CompanyLocationContext";

// interface ProductOption {
//   product: string,
//   sortOrder: number,
// }

const ProductSection = () => {
  const{ featureConfig: { enableTitleSearch }} = useConfigContext();
  const [{ stateProductConfig }] = useCompanyProductOptions();
  // const [, { getTitleSearchTransactionTypes }] = useSpaceActions();
  const [{ tsAgencyConfig }] = useSpaceAgencyConfig();
  // const [{ spaceProductSubTypes}] = useSpaceLookup();
  const { errors } = useFormState();
  const [, { getTranslations }] = useTranslation();
  const [, { getSuccessorLanguages }] = useSuccessorLanguages();
  const { getValues } = useFormWrapper();
  // const [, { getProductAgencyLocations }] = useProductAgenycLocations();
  const { sideNavState, sideNavAction } = useSideNavProvider();
  const [, { getCompanyStateLocations }] = useCompanyLocationActions();
  const [{ companyStateLocations }] = useCompanyLocation();  

  const productsRef = React.useRef<HTMLElement>();
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const hasPolicyUploadPermission = permissionCodes?.includes(PERMISSIONS.POLICYIMAGES_ACCESS) && permissionCodes?.includes(PERMISSIONS.POLICYIMAGES_SEARCH);
  const uwaTabPermissions = new Set([
    PERMISSIONS.UWA_VIEW.toString(),
    PERMISSIONS.UWA_ADDREQUEST.toString(),
    PERMISSIONS.UWA_ADDPOLICY.toString(),
    PERMISSIONS.UWA_SUBMIT.toString(),
    PERMISSIONS.UWA_REVISE.toString(),
    PERMISSIONS.UWA_CANCEL.toString(),
    PERMISSIONS.UWA_UNDOCANCEL.toString(),
    PERMISSIONS.UWA_VIEWSUMMARY.toString(),
    PERMISSIONS.UWA_SENDMESSAGEDOC.toString(),
  ])
  const hasUwaPermission = permissionCodes?.some((perm) => uwaTabPermissions.has(perm))
    
  // converted to array for performance reasons
  const [
    fileId,
    fileNameNumber,
    agencyId,
    agencyLocationId,
    stateAbbr,
    aalIsDefault,
    aalIdentityKey,
    cplIsDefault,
    cplIdentityKey,
    jacketIsDefault,
    jacketIdentityKey,
    standaloneEndorsementIsDefault,
    standaloneEndorsementIdentityKey,
  ] = useWatch({
    name: [
      "id",
      "fileNameNumber",
      "agency.id",
      "agencyLocation.id",
      "properties.0.state.abbr",
      "aALProducts.0.isDefault",
      "aALProducts.0.identityKey",
      "cpls.0.isDefault",
      "cpls.0.identityKey",
      "jackets.0.isDefault",
      "jackets.0.identityKey",
      "standaloneEndorsements.0.isDefault",
      "standaloneEndorsements.0.identityKey",
    ],
  });

  // const uniqueProductOptions = getUniqueProductOptions();
  const [availableProducts, setAvailableProducts] = React.useState<string[]>(
    []
  );

  const shouldShowSection = isProductSectionAvailable(
    availableProducts,
    fileNameNumber,
    agencyId,
    agencyLocationId,
    stateAbbr
  );

  useEffect(() => {
    const getAvailableProducts = () => {
      // Get latest products. UseWatch variables are not getting update between product issue.
      const aalsLatest = getValues("aALProducts");
      const cplsLatest = getValues("cpls");
      const jacketsLatest = getValues("jackets");
      const standaloneEndorsementsLatest = getValues("standaloneEndorsements");
      const titleSearchLatest: TitleSearchProduct[] = getValues("titleSearchProducts");

      // Existing existing Title Search product?
      const hasNonDefaultTitleSearch =
        titleSearchLatest?.length > 0 &&
        titleSearchLatest.filter(
          (ts) => ts.isDefault && ts.identityKey === 0
        ).length === 0;        

      // console.log("getValues():", getValues());
      // console.log("hasNonDefaultTitleSearch:", hasNonDefaultTitleSearch);

      const allProductOptions = [...stateProductConfig.productOptions];

      if (
        enableTitleSearch &&
        (
          (
            tsAgencyConfig &&
            tsAgencyConfig.products &&
            tsAgencyConfig.products.length > 0 &&
            tsAgencyConfig.products[0].name?.toUpperCase() === ProductType.TitleSearch
          ) ||
          hasNonDefaultTitleSearch
        )
      ) {
        const titleSearchProductOption: ProductOption = {
          companyId: tsAgencyConfig?.id || "",
          product: ProductType.TitleSearch,
          sortOrder: getProductTabSortOrder(ProductType.TitleSearch),
          formType: "",
          state: tsAgencyConfig?.state?.stateAbbr || "",
          approvedAttorney: undefined,
          secondaryAgency: undefined,
          settlementCompany: undefined,
          printAttorneyOnly: undefined,
          municipalCode: undefined,
          lenderSuccessorLanguage: undefined,
          additionalCharge: undefined,
          specialCharge: undefined,
          premiumTax: undefined,
          riskRate: undefined,
          transCode: undefined,
          default: 0,
        };
        allProductOptions.push(titleSearchProductOption);
      }      

      //1) Trim it down to a unique set
      const potentialProducts = [
        ...new Set(
          orderBy(allProductOptions, "sortOrder").map((option) => option.product)
        ),
      ];

      const hasNonDefaultAALs =
        aalsLatest?.length > 0 &&
        aalsLatest.filter(
          (entity: { isDefault: any; identityKey: number; }) =>
            entity.isDefault && entity.identityKey === 0
        ).length === 0;
      const hasNonDefaultCPLs =
        cplsLatest?.length > 0 &&
        cplsLatest.filter(
          (entity: { isDefault: any; identityKey: number; }) =>
            entity.isDefault && entity.identityKey === 0
        ).length === 0;
      const hasNonDefaultLetterProduct = hasNonDefaultAALs || hasNonDefaultCPLs;
      const hasNonDefaultJacket =
        jacketsLatest?.length > 0 &&
        jacketsLatest.filter(
          (entity: { isDefault: any; identityKey: number; }) =>
            entity.isDefault && entity.identityKey === 0
        ).length === 0;
      const hasNonDefaultSAE =
        standaloneEndorsementsLatest?.length > 0 &&
        standaloneEndorsementsLatest.filter(
          (entity: { isDefault: any; identityKey: number; }) =>
            entity.isDefault && entity.identityKey === 0
        ).length === 0;
      // const hasNonDefaultTitleSearch =
      //   titleSearchLatest?.length > 0 &&
      //   titleSearchLatest.filter(
      //     (entity: { isDefault: any; identityKey: number; }) =>
      //       entity.isDefault && entity.identityKey === 0
      //   ).length === 0;        

      // console.log("hasNonDefaultCPLs:", hasNonDefaultCPLs);
      // console.log("hasNonDefaultJacket:", hasNonDefaultJacket);

      // const hasNonDefaultUwa =
      //   uwasLatest?.length > 0 &&
      //   uwasLatest.filter(
      //     (entity: { isDefault: any; identityKey: number; }) =>
      //       entity.isDefault && entity.identityKey === 0
      //   ).length === 0;        


      //2) Go through that unique set and apply the rule that SAE is not simultaneously
      //   available with any other product unless they preexist this change.
      const productOptions: string[] = [];
      potentialProducts.forEach((product) => {
        switch (product) {
          case ProductType.Aal:
          case ProductType.Cpl:
            if (
              hasNonDefaultLetterProduct ||
              hasNonDefaultJacket ||
              !hasNonDefaultSAE
            ) {
              productOptions.push(product);
            }
            break;
          case ProductType.Jacket:
            if (
              hasNonDefaultLetterProduct ||
              hasNonDefaultJacket ||
              !hasNonDefaultSAE
            ) {
              productOptions.push(product);
            }
            break;
          case ProductType.StandaloneEndorsement:
            if (
              hasNonDefaultSAE ||
              (!hasNonDefaultJacket && !hasNonDefaultLetterProduct)
            ) {
              productOptions.push(product);
            }
            break;
          case ProductType.TitleSearch:
              if (hasNonDefaultTitleSearch || !hasNonDefaultSAE) {
                productOptions.push(product);
              }
              break;            
        }
      });

      // Alway include UWA Product
      if (hasUwaPermission) {
        productOptions.push(ProductTabNameExtension.UWARequest);
      }


      // Must have access
      if (hasPolicyUploadPermission) {
        productOptions.push(ProductTabNameExtension.PolicyUpload);
      }

      setAvailableProducts(productOptions);
    };

    if (
      stateProductConfig.productOptions.length > 0 ||
      (tsAgencyConfig &&
        tsAgencyConfig.products &&
        tsAgencyConfig.products.length > 0)
    ) {
      getAvailableProducts();
    }
  }, [
    aalIsDefault,
    aalIdentityKey,
    cplIsDefault,
    cplIdentityKey,
    getValues,
    jacketIsDefault,
    jacketIdentityKey,
    standaloneEndorsementIsDefault,
    standaloneEndorsementIdentityKey,
    stateProductConfig,
    tsAgencyConfig,
    fileId,
  ]);

  const useRefTabs = useRef<string[]>([]);
  const useRefTabsChanged = useRef<number>(Date.now());

  if (!isEqual(useRefTabs.current, availableProducts)) {
    useRefTabs.current = availableProducts;
    useRefTabsChanged.current = Date.now();
  }

  const getTabNames = useMemo(() => {
    return availableProducts.map((product) =>  {
      let tab:TabNavigatorItem = {
        tabId: "",
        title: "",
        disabled: false,
      };
      switch (product) {
        case ProductType.TitleSearch:
          //tab.title = "Title Search";
          tab.tabId = "TITLESEARCH"
          tab.title = ProductTabTitle.TitleSearch;
          break;
        case ProductType.Aal:
          // tab.title = "AAL";
          tab.tabId = "AAL"
          tab.title = ProductTabTitle.Aal;
          break;
        case ProductType.Cpl:
          // tab.title =  "CPL";
          tab.tabId = "CPL"
          tab.title = ProductTabTitle.Cpl;
          break;
        case ProductType.Jacket:
          // tab.title =  "Jacket";
          tab.tabId = "JACKET"
          tab.title = ProductTabTitle.Jacket;
          break;
        case ProductType.UWARequest:
          tab.tabId = "UWAREQUEST"
          tab.title = ProductTabTitle.UWARequest;
          break;  
        case ProductType.StandaloneEndorsement:
          // tab.title =  "Standalone Endorsement";
          tab.tabId = "SAENDO"
          tab.title = ProductTabTitle.StandaloneEndorsement;
          break;
        case ProductTabNameExtension.PolicyUpload:
          // tab.title =  "Policy Upload";
          tab.tabId = "POLICYUPLOAD"
          tab.title = ProductTabTitle.PolicyUpload;
          tab.disabled = fileId > 0 ? false : true;
          break;
        default:
          tab.title = "";
      }
      return tab;
    });
  }, [useRefTabsChanged.current, fileId]);

  const renderComponents = useMemo(() => {
    return availableProducts.map((product: string) => {
      let Component: any = null;
      switch (product) {
        case ProductType.TitleSearch:
          Component = tabMapping["TitleSearchCollection"];
          break;        
        case ProductType.Aal:
          Component = tabMapping["AALProductCollection"];
          break;
        case ProductType.Cpl:
          Component = tabMapping["CPLProductCollection"];
          break;
        case ProductType.Jacket:
          Component = tabMapping["JacketProductCollection"];
          break;
        case ProductType.StandaloneEndorsement:
          Component = tabMapping["StandAloneCollection"];
          break;
        case ProductType.UWARequest:
          Component = tabMapping["UWACollection"];
          break;
        case ProductTabNameExtension.PolicyUpload:
          Component = tabMapping["PolicyUpload"];
          break;
        default:
          break;
      }

      return Component && <Component key={product} />;
    });
  }, [useRefTabsChanged.current]);

  const isCPLError = Boolean(errors.cpls);
  const isAALError = Boolean(errors.aALProducts);
  const isJacketError = Boolean(errors.jackets);
  const isStandaloneError = Boolean(errors.standaloneEndorsements);
  const isTitleSearchError = Boolean(errors.titleSearchProducts);
  const isUnderWriterError = Boolean(errors.uwaProducts);

  const errorTabs = () => {
    const tabs: string[] = [];

    if (isTitleSearchError) tabs.push(ProductTypeName.TITLE_SEARCH);
    if (isCPLError) tabs.push(ProductTypeName.CPL);
    if (isAALError) tabs.push(ProductTypeName.AAL);
    if (isJacketError) tabs.push(ProductTypeName.JACKET);
    if (isStandaloneError) tabs.push(ProductTypeName.STANDALONE_ENDORSEMENT);
    if (isUnderWriterError) tabs.push(ProductType.UWARequest);

    return tabs;
  };

  const memoizedErrorTabs = useMemo(
    () => errorTabs(),
    [isTitleSearchError, isCPLError, isAALError, isJacketError, isStandaloneError, isUnderWriterError]
  );

  const navigateProductTabs = (evt: KeyboardEvent) => {
    if (evt.key === "Tab" && !evt.shiftKey) {
      const element = evt.target as HTMLElement;
      if (element.tabIndex < 0) {
        const nextTab = getTabNames.findIndex(tab => tab.title === element.id) + 1;
        if (nextTab < getTabNames.length) {
          evt.preventDefault();
          document?.getElementById(getTabNames[nextTab].title)?.focus();
        }
      }
    }
  };

  useEffect(() => {
    getSuccessorLanguages();
  }, []);

  useEffect(() => {
    getTranslations("STEWARTACCESS_API", "AdditionalFormDetail");
  }, [getTranslations]);

  // useEffect(() => {
  //   if (agencyId && stateAbbr) {
  //     getProductAgencyLocations(agencyId, stateAbbr);
  //   }
  // }, [agencyId, stateAbbr]);

  useEffect(() => {
    if (shouldShowSection) {
      if (sideNavState.items.get("Products & Services")?.childRef?.current) return;
      sideNavAction({
        type: ActionType.UPDATE,
        item: { name: "Products & Services", childRef: productsRef },
      });
    } else {
      sideNavAction({
        type: ActionType.UPDATE,
        item: { name: "Products & Services", childRef: undefined },
      });
    }
  }, [shouldShowSection, sideNavState.items]);

  React.useEffect(() => { 
    if (companyStateLocations?.length === 0) {
      if (agencyId && stateAbbr) {
        getCompanyStateLocations(agencyId, stateAbbr);
      }
    }
  },[companyStateLocations?.length])

  // useEffect(() => {
  //   const getTSTransactionTypes = async () => {
  //     await getTitleSearchTransactionTypes();
  //   };
  //   if (
  //     (!spaceProductSubTypes || spaceProductSubTypes.length === 0) &&
  //     tsAgencyConfig &&
  //     tsAgencyConfig.products &&
  //     tsAgencyConfig.products.length > 0
  //   ) {
  //     // Get Title Search Transaction Type
  //     getTSTransactionTypes();
  //   }
  // }, [tsAgencyConfig]);

  if (!shouldShowSection) return null;
  
  return (
    <>
      <FormSection
        navItemRef={productsRef}
        icon={<GradientIcon Icon={ShoppingCart} />}
        isError={memoizedErrorTabs.length > 0}
        isNavigator
        title="Products & Services"
      >
        <CollapsableProvider>
          <TabNavigator
            tabsWithErrors={memoizedErrorTabs}
            tabs={getTabNames}
            onKeyDown={(evt: KeyboardEvent) => {
              navigateProductTabs(evt);
            }}
          >
            {renderComponents}
          </TabNavigator>
        </CollapsableProvider>
      </FormSection>
    </>
  );
};

export default ProductSection;
